/**
 * Iter 141 — Finance Pay-Run.
 *
 * Groups open/partial vendor bills by vendor + due-date. Ideal for a
 * Friday-afternoon payment sweep: multi-select bills → mark paid in one
 * shot via the existing /bills/bulk-mark-paid endpoint.
 */
import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import EmptyState from "@/components/EmptyState";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";
import { Wallet, RefreshCw, CheckCircle2, ChevronDown, ChevronRight, AlertTriangle } from "lucide-react";

const iso = (d) => d.toISOString().slice(0, 10);
const daysFromNow = (n) => { const d = new Date(); d.setDate(d.getDate() + n); return iso(d); };

export default function FinancePayRun() {
  const { activeWorkspaceId } = useAuth();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(true);
  const [dueBefore, setDueBefore] = useState(daysFromNow(7));
  const [selectedBills, setSelectedBills] = useState(() => new Set());
  const [expanded, setExpanded] = useState(() => new Set());
  const [markConfirm, setMarkConfirm] = useState(null); // { ids, total }
  const [payAccounts, setPayAccounts] = useState([]);
  const [payAccountId, setPayAccountId] = useState("");
  const [payMethod, setPayMethod] = useState("bank");

  const load = async () => {
    if (!activeWorkspaceId) return;
    setBusy(true);
    try {
      const params = new URLSearchParams();
      if (dueBefore) params.set("due_before", dueBefore);
      const { data } = await api.get(`/workspaces/${activeWorkspaceId}/finance/pay-run?${params.toString()}`);
      setData(data);
      // Auto-expand vendors with overdue bills.
      const overdueVendors = new Set((data.vendors || []).filter((v) => v.overdue_count > 0).map((v) => v.vendor_id));
      setExpanded(overdueVendors);
    } catch (e) { toast.error(apiErrorMessage(e, "Failed to load pay-run")); }
    finally { setBusy(false); }
  };
  useEffect(() => { load(); }, [activeWorkspaceId, dueBefore]);

  // Load bank/cash accounts for the pay-selection dropdown.
  useEffect(() => {
    if (!activeWorkspaceId) return;
    api.get(`/workspaces/${activeWorkspaceId}/accounts?type=asset`)
      .then(({ data }) => {
        const bank = (data.accounts || []).filter((a) => ["bank", "cash", "current_asset"].includes(a.subtype));
        setPayAccounts(bank);
        if (bank.length && !payAccountId) setPayAccountId(bank[0].id);
      })
      .catch(() => { /* silent — surfaced when user tries to mark paid */ });
  }, [activeWorkspaceId]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleVendor = (vid) => setExpanded((prev) => {
    const next = new Set(prev);
    if (next.has(vid)) next.delete(vid); else next.add(vid);
    return next;
  });

  const toggleBill = (bid) => setSelectedBills((prev) => {
    const next = new Set(prev);
    if (next.has(bid)) next.delete(bid); else next.add(bid);
    return next;
  });

  const toggleAllVendorBills = (vendor, on) => setSelectedBills((prev) => {
    const next = new Set(prev);
    vendor.bills.forEach((b) => { if (on) next.add(b.id); else next.delete(b.id); });
    return next;
  });

  const selectedTotal = useMemo(() => {
    if (!data) return 0;
    let sum = 0;
    for (const v of data.vendors || []) {
      for (const b of v.bills || []) {
        if (selectedBills.has(b.id)) sum += b.outstanding;
      }
    }
    return sum;
  }, [data, selectedBills]);

  const markPaid = async () => {
    if (!payAccountId) {
      toast.error("Pick a payment account first");
      return;
    }
    setBusy(true);
    try {
      const ids = Array.from(selectedBills);
      const { data: res } = await api.post(`/workspaces/${activeWorkspaceId}/bills/bulk-mark-paid`, {
        bill_ids: ids,
        payment_date: iso(new Date()),
        payment_account_id: payAccountId,
        method: payMethod,
      });
      toast.success(`Marked ${res.paid ?? ids.length} bills paid${res.skipped ? ` · ${res.skipped} skipped` : ""} · ${fmtMoney(res.total_amount ?? 0)}`);
      setSelectedBills(new Set());
      setMarkConfirm(null);
      load();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Bulk mark-paid failed"));
    } finally { setBusy(false); }
  };

  return (
    <>
      <PageHeader
        eyebrow="Finance"
        title="Payroll"
        description="Every open vendor bill, grouped by vendor and sorted by overdue → soonest-due. Multi-select bills to mark paid in one shot on your weekly payment sweep."
        testid="pay-run-header"
        actions={
          <Button variant="outline" className="rounded-none" onClick={load} disabled={busy} data-testid="pay-run-refresh">
            <RefreshCw className={`w-3.5 h-3.5 mr-2 ${busy ? "animate-spin" : ""}`} /> Refresh
          </Button>
        }
      />

      <div className="px-4 sm:px-6 md:px-10 pb-10">
        {/* Filter bar */}
        <div className="border border-border bg-muted/40 p-3 flex flex-wrap items-center gap-3 mb-4" data-testid="pay-run-filters">
          <Label className="label-eyebrow text-[10px]">Include bills due before</Label>
          <Input type="date" value={dueBefore} onChange={(e) => setDueBefore(e.target.value)} className="h-8 rounded-none w-40" data-testid="pay-run-due-before" />
          <div className="flex gap-1">
            <Button size="sm" variant="ghost" className="rounded-none h-7 text-xs" onClick={() => setDueBefore(daysFromNow(7))} data-testid="pay-run-preset-7d">7d</Button>
            <Button size="sm" variant="ghost" className="rounded-none h-7 text-xs" onClick={() => setDueBefore(daysFromNow(14))} data-testid="pay-run-preset-14d">14d</Button>
            <Button size="sm" variant="ghost" className="rounded-none h-7 text-xs" onClick={() => setDueBefore(daysFromNow(30))} data-testid="pay-run-preset-30d">30d</Button>
          </div>
          <span className="text-[11px] text-muted-foreground ml-auto" data-testid="pay-run-summary">
            {data?.bills_count ?? 0} bills · {data?.vendors_count ?? 0} vendors
            {data?.overdue_count > 0 && <span className="text-red-600 ml-2">· {data.overdue_count} overdue</span>}
          </span>
        </div>

        {/* Totals strip */}
        {data && data.bills_count > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4">
            <StatTile label="Total outstanding" value={fmtMoney(data.total_outstanding)} testid="pay-run-total" />
            <StatTile label="Selected" value={fmtMoney(selectedTotal)} highlight testid="pay-run-selected" />
            <StatTile label="Overdue bills" value={data.overdue_count} color={data.overdue_count > 0 ? "text-red-600" : ""} testid="pay-run-overdue" />
            <StatTile label="Vendors" value={data.vendors_count} testid="pay-run-vendors-count" />
          </div>
        )}

        {/* Selected bulk bar */}
        {selectedBills.size > 0 && (
          <div className="border border-primary/40 bg-primary/5 px-3 py-2 mb-4 flex items-center gap-2" data-testid="pay-run-bulk-bar">
            <span className="text-xs font-semibold" data-testid="pay-run-selected-count">{selectedBills.size} selected · {fmtMoney(selectedTotal)}</span>
            <span className="text-[10px] text-muted-foreground">·</span>
            <Button size="sm" className="rounded-none h-7 text-xs"
                    onClick={() => setMarkConfirm({ ids: Array.from(selectedBills), total: selectedTotal })}
                    disabled={busy}
                    data-testid="pay-run-mark-paid-btn">
              <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Mark all paid
            </Button>
            <button type="button" onClick={() => setSelectedBills(new Set())} className="ml-auto text-[11px] text-muted-foreground hover:text-foreground underline" data-testid="pay-run-clear-selection">Clear</button>
          </div>
        )}

        {/* Vendor list */}
        {busy && !data ? (
          <div className="text-sm text-muted-foreground p-6">Loading…</div>
        ) : !data || data.vendors_count === 0 ? (
          <EmptyState
            title="Nothing to pay"
            description="No open or partial bills matched. Try widening the date range."
            icon={Wallet}
            testid="pay-run-empty"
          />
        ) : (
          <div className="border border-border divide-y divide-border">
            {data.vendors.map((v) => (
              <VendorSection
                key={v.vendor_id}
                vendor={v}
                open={expanded.has(v.vendor_id)}
                onToggle={() => toggleVendor(v.vendor_id)}
                selectedBills={selectedBills}
                onToggleBill={toggleBill}
                onToggleAll={(on) => toggleAllVendorBills(v, on)}
              />
            ))}
          </div>
        )}
      </div>

      {markConfirm && (
        <AlertDialog open onOpenChange={(o) => { if (!o) setMarkConfirm(null); }}>
          <AlertDialogContent data-testid="pay-run-confirm-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle>Mark {markConfirm.ids.length} bill{markConfirm.ids.length === 1 ? "" : "s"} as paid?</AlertDialogTitle>
              <AlertDialogDescription>
                This will record a full-outstanding payment on each bill and post the corresponding Dr AP / Cr Bank journal entries.
                Total: <strong>{fmtMoney(markConfirm.total)}</strong>.
                This action cannot be undone from this screen &mdash; you&apos;ll need to void the payment individually if you make a mistake.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <div className="space-y-3 py-2">
              <div>
                <Label className="label-eyebrow text-[10px] mb-1.5 inline-block">Payment account</Label>
                <select value={payAccountId} onChange={(e) => setPayAccountId(e.target.value)} className="h-9 w-full border border-border rounded-none text-sm px-2 bg-background" data-testid="pay-run-confirm-account">
                  {payAccounts.length === 0 && <option value="">No bank/cash accounts found</option>}
                  {payAccounts.map((a) => <option key={a.id} value={a.id}>{a.code} · {a.name}</option>)}
                </select>
              </div>
              <div>
                <Label className="label-eyebrow text-[10px] mb-1.5 inline-block">Payment method</Label>
                <select value={payMethod} onChange={(e) => setPayMethod(e.target.value)} className="h-9 w-full border border-border rounded-none text-sm px-2 bg-background" data-testid="pay-run-confirm-method">
                  <option value="bank">Bank transfer</option>
                  <option value="upi">UPI</option>
                  <option value="cheque">Cheque</option>
                  <option value="cash">Cash</option>
                  <option value="card">Card</option>
                  <option value="other">Other</option>
                </select>
              </div>
            </div>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="pay-run-confirm-cancel">Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={markPaid} disabled={busy || !payAccountId} data-testid="pay-run-confirm-run">
                {busy ? "Marking…" : "Mark paid"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </>
  );
}

function VendorSection({ vendor, open, onToggle, selectedBills, onToggleBill, onToggleAll }) {
  const allSelected = vendor.bills.length > 0 && vendor.bills.every((b) => selectedBills.has(b.id));
  const someSelected = vendor.bills.some((b) => selectedBills.has(b.id)) && !allSelected;

  return (
    <div data-testid={`pay-run-vendor-${vendor.vendor_id}`}>
      <div className="flex items-center gap-2 px-3 py-2.5 bg-muted/20 hover:bg-muted/40 transition-colors">
        <input
          type="checkbox"
          checked={allSelected}
          ref={(el) => { if (el) el.indeterminate = someSelected; }}
          onChange={() => onToggleAll(!allSelected)}
          className="w-3.5 h-3.5"
          aria-label={`Select all bills for ${vendor.vendor_name}`}
          data-testid={`pay-run-vendor-selectall-${vendor.vendor_id}`}
          onClick={(e) => e.stopPropagation()}
        />
        <button type="button" onClick={onToggle} className="flex-1 flex items-center gap-2 min-w-0 text-left" data-testid={`pay-run-vendor-toggle-${vendor.vendor_id}`}>
          {open ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
          <span className="font-semibold text-sm truncate">{vendor.vendor_name}</span>
          <span className="text-[11px] text-muted-foreground">· {vendor.bills_count} bill{vendor.bills_count === 1 ? "" : "s"}</span>
          {vendor.overdue_count > 0 && (
            <span className="chip text-[10px] chip-rejected" title="Overdue"><AlertTriangle className="w-3 h-3 inline mr-0.5" />{vendor.overdue_count} overdue</span>
          )}
        </button>
        <span className="text-xs text-muted-foreground shrink-0">next due · {vendor.next_due || "—"}</span>
        <span className="text-sm font-mono font-bold shrink-0 w-32 text-right">{fmtMoney(vendor.total_outstanding)}</span>
      </div>
      {open && (
        <div className="pl-9 pr-3 py-1 bg-background border-t border-border" data-testid={`pay-run-vendor-bills-${vendor.vendor_id}`}>
          <table className="w-full text-xs">
            <thead className="text-[10px] uppercase tracking-widest text-muted-foreground">
              <tr>
                <th className="text-left py-1 w-6"></th>
                <th className="text-left py-1">Bill #</th>
                <th className="text-left py-1">Bill date</th>
                <th className="text-left py-1">Due</th>
                <th className="text-right py-1">Amount paid</th>
                <th className="text-right py-1">Outstanding</th>
                <th className="text-left py-1 w-16">Status</th>
              </tr>
            </thead>
            <tbody>
              {vendor.bills.map((b) => (
                <tr key={b.id} className={`border-t border-border/50 ${selectedBills.has(b.id) ? "bg-primary/5" : ""}`} data-testid={`pay-run-bill-row-${b.id}`}>
                  <td className="py-1.5">
                    <input type="checkbox" checked={selectedBills.has(b.id)} onChange={() => onToggleBill(b.id)}
                           className="w-3.5 h-3.5" data-testid={`pay-run-bill-check-${b.id}`} />
                  </td>
                  <td className="py-1.5 font-mono">{b.bill_no || b.id.slice(0, 8)}</td>
                  <td className="py-1.5 font-mono">{b.bill_date}</td>
                  <td className={`py-1.5 font-mono ${b.is_overdue ? "text-red-600 font-bold" : ""}`}>{b.due_date || "—"}</td>
                  <td className="py-1.5 text-right font-mono">{fmtMoney(b.amount_paid, b.currency)}</td>
                  <td className="py-1.5 text-right font-mono font-semibold">{fmtMoney(b.outstanding, b.currency)}</td>
                  <td className="py-1.5"><span className="chip text-[10px]">{b.status}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function StatTile({ label, value, color = "", highlight, testid }) {
  return (
    <div className={`border p-3 ${highlight ? "border-primary bg-primary/10" : "border-border"}`} data-testid={testid}>
      <div className="label-eyebrow text-[10px]">{label}</div>
      <div className={`text-xl font-bold mt-0.5 font-mono ${color}`}>{value}</div>
    </div>
  );
}

function fmtMoney(v, cur = "INR") {
  if (v == null) return "—";
  return `${cur} ${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}
